import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component, ElementRef,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output, ViewChild
} from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { Subject } from 'rxjs/Subject';
import { cloneDeep, get, set, isEmpty } from 'lodash';
import { distinctUntilChanged, filter, flatMap, map, switchMap, take, takeUntil, tap } from 'rxjs/operators';
import * as immutable from 'immutable';
import * as jmespath from 'jmespath';

import { AgregationService } from '@ispm-client/modules/aggregator/services';
import { ConfigurationStoreHandler } from '../../../configuration/services/configuration-store-handler.service';
import { CoreActionCreatorService } from '@ispm/core/services/core-action-creator.service';
import { DEFAULT_THERAPY_EDITING_CONFIGURATION } from '@ispm/therapies/constants/default-therapy-editing-configuration.constant';
import { DynamicModalLoaderService } from '@ispm/layout/services/dynamic-modal-loader.service';
import { EditTherapyModalComponent } from '@ispm/therapies/components/edit-therapy-modal/edit-therapy-modal.component';
import { FormHandlerService } from '@ispm/dynamic-form/services/form-handler.service';
import { IDefinition } from '../../../configuration/interfaces/definition.interface';
import { IPathValue, ISelectionResource } from '@ispm/therapies/interfaces/view-configuration.interface';
import { InterpolateService } from '@ispm/shared/services/interpolate.service';
import { ModalTypes } from '@ispm/layout/components/modals/contants/modal-types.enum';
import { PlaceholderModalComponent } from '@ispm/layout/components/modals/placeholder-modal/placeholder-modal.component';
import { PlaceholderModalLoaderPayload } from '@ispm/layout/classes/placeholder-modal-loader-payload';
import { TherapiesActionCreatorService } from '@ispm/therapies/services/therapies-action-creator.service';
import { TherapiesService } from '@ispm/therapies/services/therapies.service';
import { TherapyEditPayload } from '@ispm/therapies/classes/therapy-edit-payload.class';
import { TreatmentSelectionService } from '@ispm/therapies/services/treatment-selection.service';
import { modalResponse } from '@ispm/layout/constants/modal-responses.enum';
import { Workflow } from "@ispm/core/interfaces/workflow.interface";
import { WorkflowConfigService } from "@ispm/workflow/services/workflow-config.service";
import { ISubjectsAppearance } from "@ispm/therapies/interfaces/subject.interface";
import { WorkflowService } from "@ispm/workflow/services/workflow.service";
import { combineLatest } from "rxjs";
import { BehaviorSubject } from "rxjs/BehaviorSubject";
import {TestService} from "@ispm/test/services/test.service";
import {AuthenticationService} from "@ispm/user/services/authentication.service";

export interface ICssClasses {
  subject?: string;
  title?: string;
  body?: string;
  dataScroller?: string;
  row?: string;
}

@Component({
  selector: 'gen-treatment-subject-list',
  templateUrl: './treatment-subject-list.component.html',
  styleUrls: ['./treatment-subject-list.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})

export class TreatmentSubjectListComponent implements OnInit, OnDestroy {

  @Input()
  public subjectId: string;

  @Input()
  public autoSelect: boolean;

  @Input()
  public set therapiesGroups(therapiesGroups: any[]) {
    this._therapiesGroups = therapiesGroups;
    this.therapiesGroups$.next(therapiesGroups);
  }

  public get therapiesGroups(): any[] {
    return this._therapiesGroups;
  }

  // warn: widget payload breaks ISelectionResource
  @Input()
  public subjectListConfig: ISelectionResource;

  private _therapiesGroups: any[] = [];

  @Input()
  set selectOnInit(select) {
    if (select == false) {
      // the default option is to enable initial selection
      this._selectOnInit = select;
    }
  }

  get selectOnInit() {
    return this._selectOnInit;
  }

  private _selectOnInit: boolean = true;

  private changeOpinionOfMI: boolean = false;

  @Input()
  public selectionIdentifier: IPathValue;

  @Input()
  public editVisible: boolean;

  @Input()
  //The id of the dynamic form that contains this gen-treatment-subject-list.
  public formId: string;

  @Input()
  public isEditable: boolean;

  @Input()
  public molecularFinding: any[];

  @Input()
  public set showInReportToggleActive(show: boolean) {
    this.subjectsAppearance.showInToggleActive.report = show;
  }

  @Input()
  public set showInMIToggleActive(show: boolean) {
    this.subjectsAppearance.showInToggleActive.MI = show;
  }

  @Output()
  public onSubjectSelect: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public onSubjectInit: EventEmitter<string> = new EventEmitter<string>();

  @Output()
  public onchangeOpinionOfMI: EventEmitter<boolean> = new EventEmitter<boolean>();

  @Output()
  public onSubjectUpdated: EventEmitter<string> = new EventEmitter<string>();

  @ViewChild('pScroller', { static: false, read: ElementRef}) pScrollerRef: ElementRef;

  public smallSizeTypes = ['include_in_report', 'counter', 'tier'];
  public inPrevPriorityList: { [prop: string]: any } = { };
  public maxSelection: any = 1;
  public subjectsAppearance: ISubjectsAppearance = {
    subjects: { },
    includeInDisabled: { },
    showInToggleActive: { },
  };
  public maxHeight: string;
  public isScrollable: boolean = true;
  public testConfiguration: IDefinition;
  private therapiesGroups$: BehaviorSubject<any[]> = new BehaviorSubject<any[]>([]);
  private destroyer$: Subject<void> = new Subject<void>();
  private subjectsGroupDestroyer$: Subject<void> = new Subject<void>();
  public therapiesAndTrailCalculatedHeight$: BehaviorSubject<string>;

  public loading: boolean = false;

  constructor(
    private therapyAC: TherapiesActionCreatorService,
    private cd: ChangeDetectorRef,
    private modalLoader: DynamicModalLoaderService,
    private _formHandler: FormHandlerService,
    private configurationAC: ConfigurationStoreHandler,
    private _aggregationService: AgregationService,
    private coreAC: CoreActionCreatorService,
    private _treatmentSelectionService: TreatmentSelectionService,
    private workflowConfigService: WorkflowConfigService,
    private testService: TestService,
    private authService: AuthenticationService,
    private workflowService: WorkflowService,
  ) {
  }

  private emitSelection() {
    if (this.therapiesGroups && this.therapiesGroups.length > 0 && this.autoSelect && !this.subjectId) {
      if (this.subjectListConfig.selectionIdentifier) {
        this.therapyAC.getPriorityList().take(1).subscribe(priority => this.emitFromPriority(priority));
      } else {
        this.onSubjectInit.emit(this.therapiesGroups[0].id);
        this._treatmentSelectionService.updateSelection(this.therapiesGroups[0]);
      }
    }
    else {
      if (this.therapiesGroups && !this.therapiesGroups.length && !this.subjectId) {
        this.updateSubject(null);
      }
    }
  }

  ngOnInit() {
    this.subjectsAppearance.isMDA = this.workflowService.isMDA();

    combineLatest([
      this.configurationAC.getCurrentConfig(),
      this.therapiesGroups$,
    ])
    .pipe(
      tap(([testConfiguration, therapiesGroups]) => {
        // Adding prefix text to 'RnaSpliceVariants' aberrations.
        // Better to make it configurable
        // TODO: move to treatment.subject.component or apply for therapies, and not for aberration groups
        therapiesGroups.forEach(subject => {
          if ((subject.aberration?.AberrationClass === 'RnaSpliceVariants') &&
            (!String(subject.alteration).trim().toLowerCase().startsWith('rna splice variant'))
          ) {
            subject.alteration = 'RNA splice variant: ' + subject.alteration;
          }
        });
        this.testConfiguration = testConfiguration;
        this.cd.markForCheck();
      }),
      switchMap(() => {
        if ((this.subjectListConfig as any).selectOnInit !== false) {
          this.emitSelection();
        }

        return combineLatest([
          this.therapyAC.getIncludedMap().pipe(
            tap(includeMap => this.defineSubjectsVisibilities(includeMap))
          ),
          this.therapyAC.getPriorityList().pipe(
            tap(treatmentsPriority => {
              this.updateMandatorySubjects(treatmentsPriority.toArray());
              this.assignPriorities(treatmentsPriority.toArray());
            })
          ),
          this._formHandler.accordionSelectionChanged$.pipe(
            filter(selectedItem => !!selectedItem),
            tap((selectedItem) => {
              // The accordionSelectionChanged event is sent to all the TreatmentSubjectListComponent.
              // By comparing the itemId to DynamicFormName we know if the current TreatmentSubjectListComponent was selected.
              if (selectedItem.open && selectedItem.itemId === this.formId) {
                if (this.subjectId) {
                  this.updateSubject(this.subjectId, true); // Selecting again the last selected item of the current tab
                } else {
                  this.emitSelection(); // If no item was selected before select the first item
                }
              }
            })
          ),
        ]);
      }),
      takeUntil(this.destroyer$.asObservable())
    ).subscribe();

    this.therapyAC.getTreatmentGlobalConfiguration().pipe(
      take(1),
      tap((configuration: immutable.Map<string, any>) => {
        this.maxSelection = configuration.get('maxSelections');
        this.maxHeight = configuration.get('maxHeight') || "35vh";
      })
    ).subscribe();

    // widget payload breaks ISelectionResource
    if ((this.subjectListConfig as any).selectionNotification === 'service') {
      this._treatmentSelectionService.selection
        .pipe(
          takeUntil(this.destroyer$.asObservable()),
          tap((subject) => {
            if (subject.id !== this.subjectId) {
              this.subjectId = null;
              this.cd.markForCheck();
              this.cd.detectChanges();
            }
          })
        ).subscribe();
    }

    this.workflowConfigService.getRequestedRevision().pipe(
      takeUntil(this.destroyer$.asObservable()),
      tap((requestedRevision) => {
        if (Workflow.checkCurrentStatus(requestedRevision, ['test completed'])) {
          this.subjectsAppearance.includeInDisabled.report = true;
        }
      }),
    ).subscribe();

    this.therapiesAndTrailCalculatedHeight$ = this._treatmentSelectionService.therapiesAndTrailCalculatedHeight$;
    if(this.subjectsAppearance.isMDA && !this.isUserCannotChooseAberrationToMI())
    {
      this.updateMolecularFinding();
    }
  }

  private isUserCannotChooseAberrationToMI(){
    if (!this.testService) {
      return;
    }
    const curationEnabled =this.testService.testConfiguration?this.testService.testConfiguration['AllowSendToCurationUserList']:undefined;
    if (curationEnabled && curationEnabled.length > 0 && this.workflowService.currentTest.workflowRequest.activeJobName == "UiManualAberrationCuration" )
      return !(curationEnabled && curationEnabled.indexOf(this.authService.user.info.id) > -1 );
    return true;
  }

  private updateMolecularFinding(){
    this.molecularFinding.forEach(subject=>{
        this.therapyAC.updateInclusionOpinion('MI', subject.id, true)
          .pipe(take(1))
          .subscribe(() => {
            this.onchangeOpinionOfMI.emit(!this.changeOpinionOfMI);
            this.changeOpinionOfMI = !this.changeOpinionOfMI;
          });
    })
  }

  updateMandatorySubjects(therapies: { subjectId?: string }[]) {
    therapies.forEach(therapy => {
      if (therapy.subjectId) {
        if (!this.subjectsAppearance.subjects[therapy.subjectId].mandatory.report) {
          this.toggleSelection('report', therapy.subjectId, true, true);
        }
        this.subjectsAppearance.subjects[therapy.subjectId].mandatory.report = true;

        if (!this.subjectsAppearance.subjects[therapy.subjectId].mandatory.MI) {
          this.toggleSelection('MI', therapy.subjectId, true, true);
        }
        this.subjectsAppearance.subjects[therapy.subjectId].mandatory.MI = true;
      }
    });
  }

  assignPriorities(therapies: any[]) {
    therapies.forEach(therapy => {
      if (this.subjectsAppearance.subjects[therapy.id]) {
        this.subjectsAppearance.subjects[therapy.id].therapy = therapy;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyer$.next();
    this.destroyer$.complete();
    this.subjectsGroupDestroyer$.next();
    this.subjectsGroupDestroyer$.complete();
  }

  // @deprecated
  trackByField(field = 'id') {
    return function (index, item) {
      if (!item) {
        return index;
      }

      return field
        ? get(item, field, index)
        : item;
    };
  }

  updateSubject(subjectId: string, force?: boolean) {
    if (force) {
      this.updateSubject(null);
    }

    this.subjectId = subjectId;
    this.onSubjectSelect.emit(this.subjectId);
    this._treatmentSelectionService.updateSelection(this.therapiesGroups.find(subject => subject.id === this.subjectId));
  }

  therapyClickedEvent(subject: any) {
    if (!this.maxSelection || this.maxSelection == 1) {
      // this._treatmentSelectionService.setSpinnerActive(true);
      this.updateSubject(subject.id);
    }
    this.cd.markForCheck();
  }

  therapyCheckedEvent(subject: any, isSelected: boolean = true) {
    // If therapy already selected update store directly
    if (this.subjectId === subject.id) {
      if (isSelected && !this.subjectsAppearance.subjects[subject.id].therapy) {
        this.therapyAC.addTherapy(this.inPrevPriorityList[subject.id]);
      } else if (!isSelected) {
        this.therapyAC.removeTherapyFromPriority(this.subjectsAppearance.subjects[subject.id].therapy);
      }

      return;
    }
    // This will update selection and update the store
    this.updateSubject(subject.id);
  }

  toggleSelection(selectionIn: 'report' | 'MI', subjectId: string, state?: boolean, force: boolean = false) {
    if (
      !force &&
      (
        !this.subjectsAppearance.showInToggleActive[selectionIn]
        || this.subjectsAppearance.subjects[subjectId].mandatory[selectionIn]
        || this.subjectsAppearance.subjects[subjectId].visible[selectionIn] === null
      )
    ) {
      // disabled or in loading mode
      return;
    }

    if (state === undefined) {
      //  in-case state was not provided we are toggling the state
      state = !this.subjectsAppearance.subjects[subjectId].visible[selectionIn];
    }

    if (this.subjectsAppearance.subjects[subjectId].visible[selectionIn] !== state) {
      this.subjectsAppearance.subjects[subjectId].visible[selectionIn] = null; // null - needs for loading circle
      this.subjectsAppearance = { ...this.subjectsAppearance};
      this.cd.markForCheck();

      this.therapyAC.updateInclusionOpinion(selectionIn, subjectId, state)
        .pipe(take(1))
        .subscribe(() => {
          if (this.subjectsAppearance.isMDA && selectionIn === 'MI') {
            this.onchangeOpinionOfMI.emit(!this.changeOpinionOfMI);
            this.changeOpinionOfMI = !this.changeOpinionOfMI;
          }
          this.subjectsAppearance.subjects[subjectId].visible[selectionIn] = state;
          this.subjectsAppearance = { ...this.subjectsAppearance};

          this.onSubjectUpdated.emit();
          this.cd.markForCheck();
        });
    }
  }

  defineSubjectsVisibilities(includeMap: { [includeIn in 'report' | 'MI']: { [prop: string]: { visible: boolean } } }) {

    const definePerSubject = (subject, visibleIn: 'report' | 'MI', currentViewInSettings) => {
      if (visibleIn === 'report') {
        this.subjectsAppearance.subjects[subject.id].visible[visibleIn] = true;
      }

      if (currentViewInSettings) {
        this.subjectsAppearance.subjects[subject.id].visible[visibleIn] = currentViewInSettings[this.formId].default;

        const agnosticToGeneticsData = this.subjectListConfig.fields.find(field =>
          field.type === 'table' && !isEmpty(field.path) && isEmpty(jmespath.search(subject, field.path))
        );
        if (!!agnosticToGeneticsData) {
          this.subjectsAppearance.subjects[subject.id].visible[visibleIn] = currentViewInSettings[this.formId].empty;
        }

        if (get(includeMap[visibleIn], subject.id, false) === false && this.subjectsAppearance.subjects[subject.id].visible[visibleIn] === false) {
          this.therapyAC.updateInclusionOpinion(visibleIn, subject.id, this.subjectsAppearance.subjects[subject.id].visible[visibleIn])
            .pipe(take(1))
            .subscribe();
        }
      }

      if (get(includeMap[visibleIn], subject.id, false)) {
        this.subjectsAppearance.subjects[subject.id].visible[visibleIn] = get(includeMap[visibleIn], subject.id).visible;
      }
    };

    const currentViewInReportSettings = get(
      this.testConfiguration,
      'TreatmentLayout.subject_selection_section.formConf.defaultViewInReport',
      []
    ).find(viewSetting => !isEmpty(viewSetting[this.formId]));

    const currentViewInMISettings = get(
      this.testConfiguration,
      'TreatmentLayout.subject_selection_section.formConf.defaultViewInMI',
      []
    ).find(viewSetting => !isEmpty(viewSetting[this.formId]));


    this.subjectsAppearance.subjects = { };
    this.therapiesGroups.forEach(subject => {
      this.subjectsAppearance.subjects[subject.id] = {
        mandatory: { },
        visible: { },
        therapy: undefined,
      };

      definePerSubject(subject, 'report', currentViewInReportSettings);
      definePerSubject(subject, 'MI', currentViewInMISettings);
    });

    this.subjectsAppearance = { ...this.subjectsAppearance};
    this.cd.markForCheck();
  }

  private getEditingConfiguration(configuration: IDefinition) {
    return {
      title: get(configuration, 'therapyEditing.title', DEFAULT_THERAPY_EDITING_CONFIGURATION.title),
      forms: get(configuration, 'therapyEditing.forms', DEFAULT_THERAPY_EDITING_CONFIGURATION.forms),
      approveBtn: get(configuration, 'therapyEditing.buttons.approve', DEFAULT_THERAPY_EDITING_CONFIGURATION.buttons.approve),
      declineBtn: get(configuration, 'therapyEditing.buttons.decline', DEFAULT_THERAPY_EDITING_CONFIGURATION.buttons.decline)
    };
  }

  openEditTherapy(subject, config?: any) {
    this.configurationAC.getCurrentConfig()
      .pipe(
        take(1),
        takeUntil(this.destroyer$.asObservable()),
        flatMap((configuration: IDefinition) => {
          const editingConfiguration = this.getEditingConfiguration(configuration);
          subject = cloneDeep(subject);
          delete subject.trials;

          if (config && config.popUpConfig) {
            return this._formHandler.popupDynamicFormModal(
              config.popUpConfig.title,
              config.popUpConfig.formId,
              subject,
              config.popUpConfig.approveBtnTitle,
              config.popUpConfig.declineBtnTitle,
              null,
              TherapiesService.ModalLoaderTunnelName
            );
          } else {
            return this.modalLoader.popupModal(
              PlaceholderModalComponent,
              new PlaceholderModalLoaderPayload(
                InterpolateService.interpolate(editingConfiguration.title, subject, false, {
                  useEmptyValues: true,
                  usePartialPath: false,
                  clearUnresolvedPatterns: true
                }),
                EditTherapyModalComponent,
                editingConfiguration.approveBtn,
                editingConfiguration.declineBtn,
                new TherapyEditPayload(subject, configuration.id, editingConfiguration.forms)
              ),
              ModalTypes.NONE,
              TherapiesService.ModalLoaderTunnelName
            );
          }
        })
      )
      .subscribe((response: any) => {
        switch (response.payload && (response.payload.data || response.payload.responseType)) {
          case modalResponse.approve:
            const formData = [];
            // TODO : Bellow is a temporary solution until Sudershan finish supporting aggregation service in dynamic form
            if (config.popUpConfig) {
              const data = response.payload.formData;
              const updatedFieldsNum = get(response, 'payload.updatedFields.length', 0);
              const modified = data.modified || updatedFieldsNum > 0;
              set(data, 'modified', modified);
              const formDataGen = Object.assign(data, {
                identifier: config.popUpConfig.identifier,
                formId: config.popUpConfig.formId
              });

              if (updatedFieldsNum > 0) {
                this.coreAC.setLoader(true);
                formData.push({ formId: config.popUpConfig.formId, formdata: [formDataGen]});
                this._aggregationService.UpdateDataToServer(formData, [])
                  .pipe(
                    filter(res => res.code === 2),
                    take(1),
                  )
                  .subscribe(() => {
                    if (this.subjectId === subject.id) {
                      this.updateSubject(subject.id, true);
                    }
                    this.coreAC.setLoader(false);
                    this.cd.markForCheck();
                  });
              }
            }

            if (response.payload.data) {
              if (response.payload.payload) {
                Object.assign(subject, response.payload.payload.therapy);
              }

              if (this.subjectId === subject.id) {
                this.updateSubject(subject.id, true);
              }

              this.cd.markForCheck();
              this.onSubjectUpdated.emit();
            }
            break;
        }
      });
  }

  trackByIndex(index: number) {
    return index;
  }

  getRowClass(isActive, rowClass, edited) {
    const res = rowClass ? rowClass : 'list-row-item ';

    if (isActive) {
      return res + ' active';
    } else if (edited && edited === true) {
      return res + ' edited';
    } else {
      return res;
    }
  }

  private emitFromPriority(priority: any) {
    const priorityObj = priority.toArray()[0] || { };

    if (!isEmpty(priorityObj)) {
      const priorityTypeFit = get(priorityObj, this.subjectListConfig.selectionIdentifier.path) === this.subjectListConfig.selectionIdentifier.value;

      if (priorityTypeFit) {
        this.onSubjectInit.emit(priorityObj.id);
        this._treatmentSelectionService.updateSelection(priorityObj);
        return;
      }
    }

    this.onSubjectInit.emit(this.therapiesGroups[0].id);
    this._treatmentSelectionService.updateSelection(this.therapiesGroups[0]);
  }
}
